/**
 * A list beside the thing it selects: the default control for drilling into one
 * item of a collection.
 *
 * The master is one bordered surface with its own scroll region and a header
 * that stays put, so a long list scrolls against a stable detail pane rather
 * than running the page down past it. Its items are list items, not table
 * rows, and the whole item is the target.
 *
 * Below `lg` two columns do not fit, so the selection decides which pane is on
 * screen rather than squeezing both: the list, or the detail with a way back
 * to it. That works because the selection is addressable — the caller keeps it
 * in the URL — so "back" is a real destination and not lost state.
 *
 * @module components/ui/MasterDetail
 */

import React, { type ReactNode, useEffect, useRef } from "react";

import { Link } from "react-router";

import { Button } from "./Button";
import { EditLockExempt } from "./EditLock";
import { Icon } from "./Icon";
import { BELOW_LG, useMediaQuery } from "./useMediaQuery";

export interface MasterDetailProps {
  /** Names the list region, e.g. `Programmes`. */
  label: string;
  /** The list: `MasterList` or `MasterGroup`s of `MasterItem`s. */
  master: ReactNode;
  /**
   * Stays above the scrolling list, for what narrows it: a collapsed filter
   * row carrying the count. Inside the master's surface, never a card above it.
   */
  masterHeader?: ReactNode;
  /** Pinned below the scrolling list, for paging. Renders nothing when empty. */
  masterFooter?: ReactNode;
  /** The selection, or the empty state when there is none. */
  detail: ReactNode;
  /** Whether anything is selected. Below `lg` this picks the pane. */
  selected: boolean;
  /**
   * Opens the first item when nothing is selected, on wide screens only: below
   * `lg` the list is the first thing a phone should show. `onSelect` should
   * replace the history entry, so Back does not return to the empty pane.
   */
  autoSelectFirst?: { firstId: string | undefined; onSelect: (id: string) => void };
  /** Back to the list, for the narrow layout where only one pane fits. */
  onBack?: () => void;
  backLabel?: string;
  /** The list column's width above `lg`. */
  masterWidth?: string;
  "data-testid"?: string;
}

/**
 * @example
 * ```tsx
 * <MasterDetail label="Programmes" selected={Boolean(id)} onBack={toList}
 *   master={<MasterList>…</MasterList>} detail={<TeamPanel … />} />
 * ```
 */
export function MasterDetail({
  label,
  master,
  masterHeader = undefined,
  masterFooter = undefined,
  detail,
  selected,
  autoSelectFirst = undefined,
  onBack = undefined,
  backLabel = "Back to the list",
  masterWidth = "320px",
  "data-testid": testId,
}: MasterDetailProps): React.ReactElement {
  const narrow = useMediaQuery(BELOW_LG);
  const firstId = autoSelectFirst?.firstId;
  const autoSelect = autoSelectFirst?.onSelect;
  useEffect(() => {
    if (!narrow && !selected && firstId !== undefined) {
      autoSelect?.(firstId);
    }
  }, [narrow, selected, firstId, autoSelect]);

  // Below `lg` the pane that held focus is hidden when the selection changes,
  // which drops focus to the body. Choosing moves it into the detail; Back
  // returns it to the item that was chosen.
  const detailRef = useRef<HTMLElement>(null);
  const masterRef = useRef<HTMLElement>(null);
  const lastItem = useRef<HTMLElement | null>(null);
  const wasSelected = useRef(selected);
  useEffect(() => {
    if (narrow && selected !== wasSelected.current) {
      if (selected) {
        // Named for the item that was chosen, so focus does not land on an
        // unlabelled region.
        const chosen = masterRef.current?.querySelector("[aria-current]");
        const name =
          chosen?.getAttribute("aria-label") ??
          chosen?.querySelector(".font-semibold")?.textContent ??
          label;
        detailRef.current?.setAttribute("aria-label", name);
        detailRef.current?.focus();
      } else {
        lastItem.current?.focus();
      }
    }
    wasSelected.current = selected;
  }, [narrow, selected, label]);

  return (
    <div
      className="grid gap-4 [--sq-pane:clamp(24rem,calc(100dvh-14rem),56rem)] lg:grid-cols-[var(--sq-master)_minmax(0,1fr)] [&>*]:min-w-0"
      style={{ "--sq-master": masterWidth } as React.CSSProperties}
      data-testid={testId}
    >
      {/* Hidden rather than unmounted, so the list keeps its scroll position
          and its filters when the narrow layout comes back to it. */}
      <section
        ref={masterRef}
        aria-label={label}
        className={selected ? "max-lg:hidden" : ""}
        onFocus={(event) => {
          lastItem.current = event.target as HTMLElement;
        }}
        data-testid={testId ? `${testId}-master` : undefined}
      >
        <div className="flex flex-col overflow-hidden rounded-box border border-base-300 bg-base-100 lg:sticky lg:top-2 lg:h-[var(--sq-pane)]">
          {masterHeader && <div className="shrink-0 border-b border-base-300">{masterHeader}</div>}
          <div className="relative min-h-0 flex-1">
            <div
              className="h-full overflow-y-auto pb-6"
              data-testid={testId ? `${testId}-scroll` : undefined}
            >
              {master}
            </div>
            {/* A fade at the foot says there is more below the fold. */}
            <div
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-base-100 to-transparent"
            />
          </div>
          {/* `empty:hidden`: a pager with one page renders nothing, and its
              border should go with it. */}
          {masterFooter && (
            <div className="shrink-0 border-t border-base-300 px-3 py-2 empty:hidden">
              {masterFooter}
            </div>
          )}
        </div>
      </section>
      <section
        ref={detailRef}
        tabIndex={-1}
        className={`flex flex-col outline-none lg:min-h-[var(--sq-pane)] ${selected ? "" : "max-lg:hidden"}`}
        data-testid={testId ? `${testId}-detail` : undefined}
      >
        {/* Only where the list is off screen. Above `lg` it sits beside the
            detail, so a back control there points at what is already there. */}
        {selected && onBack && (
          <EditLockExempt>
            <Button
              variant="ghost"
              size="sm"
              icon="arrow-left"
              className="mb-3 self-start lg:hidden"
              onClick={onBack}
              data-testid={testId ? `${testId}-back` : undefined}
            >
              {backLabel}
            </Button>
          </EditLockExempt>
        )}
        {detail}
      </section>
    </div>
  );
}

/** The items of a master list, divided from each other. */
export function MasterList({
  children,
  "data-testid": testId,
}: {
  children: ReactNode;
  "data-testid"?: string;
}): React.ReactElement {
  return (
    <ul className="divide-y divide-base-300" data-testid={testId}>
      {children}
    </ul>
  );
}

/** A titled run of items, for a list the reader scans by kind. */
export function MasterGroup({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}): React.ReactElement {
  return (
    <section>
      <h3 className="sticky top-0 z-[1] border-b border-base-300 bg-base-200 px-4 py-1.5 text-xs font-semibold tracking-[0.06em] text-muted uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

export interface MasterItemProps {
  /** What the item is. Gets the room the row has, wrapping rather than cutting. */
  title: ReactNode;
  /** One line under the title. */
  meta?: ReactNode;
  /** State and counts, under the meta. Left out when every item would say the same. */
  children?: ReactNode;
  selected?: boolean;
  /** Where it goes, for a master whose selection is a route. */
  to?: string;
  onSelect?: () => void;
  /** Names the item where reading its chips aloud would not. */
  "aria-label"?: string;
  /** On the control, which is what a test presses. */
  "data-testid"?: string;
  /** On the list item around it, for a screen whose spec reads the row. */
  rowTestId?: string;
}

const ITEM_CLASS =
  "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-hover";

/**
 * One item of a master list: the whole row opens it, and a vertically
 * centred chevron says so.
 */
export function MasterItem({
  title,
  meta = undefined,
  children = undefined,
  selected = false,
  to = undefined,
  onSelect = undefined,
  "aria-label": label = undefined,
  "data-testid": testId,
  rowTestId = undefined,
}: MasterItemProps): React.ReactElement {
  const content = (
    <>
      <span className="min-w-0 flex-1">
        <span className="block font-semibold break-words text-base-content">{title}</span>
        {meta != null && <span className="mt-0.5 block text-[13px] text-muted">{meta}</span>}
        {children != null && <span className="mt-1.5 flex flex-wrap gap-1.5">{children}</span>}
      </span>
      <Icon name="caret-right" className="shrink-0 text-muted" aria-hidden />
    </>
  );
  const classes =
    `${ITEM_CLASS} ${selected ? "bg-surface-hover shadow-[inset_3px_0_0_var(--color-primary)]" : ""}`.trim();
  return (
    <li data-testid={rowTestId}>
      {to ? (
        <Link
          to={to}
          className={classes}
          aria-current={selected ? "page" : undefined}
          aria-label={label}
          data-testid={testId}
        >
          {content}
        </Link>
      ) : (
        <button
          type="button"
          className={classes}
          aria-current={selected ? "true" : undefined}
          aria-label={label}
          onClick={onSelect}
          data-testid={testId}
        >
          {content}
        </button>
      )}
    </li>
  );
}
